/**
 * GenerationPanel Component - Unified AI Generation Interface
 * Combines prompt management, generation, history, and editing in one streamlined component
 */

import React, { useState, useEffect } from 'react'
import {
  BeakerIcon,
  CheckCircleIcon,
  SparklesIcon,
  PencilSquareIcon,
  ClockIcon,
  EyeIcon,
  UserIcon
} from '@heroicons/react/24/outline'
import {
  useGenerateAppreciation,
  useSaveAppreciation,
  usePrompts,
  useCreatePrompt,
  useActivatePrompt,
  useDeletePrompt,
  useHistory
} from '../hooks/useIA'

interface GenerationPanelProps {
  affaireId: number | null
  adresse: string | null
  onEditingStateChange?: (isEditing: boolean) => void
}

export const GenerationPanel: React.FC<GenerationPanelProps> = ({
  affaireId,
  adresse,
  onEditingStateChange
}) => {
  // UI State
  const [isEditingPrompt, setIsEditingPrompt] = useState(false)
  const [isCreatingPrompt, setIsCreatingPrompt] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [showPromptHistory, setShowPromptHistory] = useState(false)
  const [showActivePrompt, setShowActivePrompt] = useState(false)
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null)
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null)

  // Notify parent when editing state changes
  useEffect(() => {
    if (onEditingStateChange) {
      onEditingStateChange(isCreatingPrompt)
    }
  }, [isCreatingPrompt, onEditingStateChange])

  // Generation State
  const [generatedText, setGeneratedText] = useState<string | null>(null)
  const [editedText, setEditedText] = useState<string>('')
  const [isEditingGenerated, setIsEditingGenerated] = useState(false)

  // Prompt State
  const [newPromptText, setNewPromptText] = useState('')
  const [newPromptComment, setNewPromptComment] = useState('')

  // Queries and Mutations
  const { data: prompts = [] } = usePrompts()
  const { data: history = [] } = useHistory(affaireId)
  const generateMutation = useGenerateAppreciation()
  const saveMutation = useSaveAppreciation()
  const createPrompt = useCreatePrompt()
  const activatePrompt = useActivatePrompt()
  const deletePrompt = useDeletePrompt()

  // Find active prompt
  const activePrompt = prompts.find(p => p.isActive)
  const promptHistory = prompts.filter(p => !p.isActive)

  // Update edited text when generated
  useEffect(() => {
    if (generatedText) {
      setEditedText(generatedText)
      setIsEditingGenerated(true)
    }
  }, [generatedText])

  const handleGenerate = async () => {
    if (!affaireId || !activePrompt || !adresse) {
      alert('Configuration incomplète')
      return
    }

    const fullPrompt = `${activePrompt.promptText}\n\nAdresse du bien: ${adresse}`

    try {
      const result = await generateMutation.mutateAsync({
        affaireId,
        fullPrompt,
        promptId: activePrompt.id
      })

      if (result) {
        setGeneratedText(result.generatedText)
      }
    } catch (error) {
      console.error('Generation error:', error)
    }
  }

  const handleSave = async () => {
    if (!affaireId || !editedText.trim()) {
      alert('Aucun texte à sauvegarder')
      return
    }

    try {
      await saveMutation.mutateAsync({
        affaireId,
        appreciationText: editedText.trim()
      })

      setGeneratedText(null)
      setEditedText('')
      setIsEditingGenerated(false)
    } catch (error) {
      console.error('Save error:', error)
    }
  }

  const handleCreatePrompt = async () => {
    if (newPromptText.trim().length < 10) {
      alert('Le prompt doit contenir au moins 10 caractères')
      return
    }

    await createPrompt.mutateAsync({
      promptText: newPromptText.trim(),
      comment: newPromptComment.trim() || undefined
    })

    setNewPromptText('')
    setNewPromptComment('')
    setIsCreatingPrompt(false)
    setIsEditingPrompt(false)
  }

  const handleActivatePrompt = async (promptId: string) => {
    await activatePrompt.mutateAsync(promptId)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'À l\'instant'
    if (diffMins < 60) return `Il y a ${diffMins} min`
    if (diffHours < 24) return `Il y a ${diffHours}h`
    if (diffDays === 1) return 'Hier'
    if (diffDays < 7) return `Il y a ${diffDays} jours`

    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const canGenerate = affaireId && activePrompt && adresse

  return (
    <div className="w-full bg-white rounded-2xl shadow-md border-2 border-gray-200 overflow-hidden">
      {/* FOCUS MODE: Editing Generated Text */}
      {isEditingGenerated && generatedText ? (
        <div>
          <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-bold text-green-900">Appréciation générée</span>
            </div>
            <span className="text-xs text-green-700 font-medium">Affaire #{affaireId}</span>
          </div>

          <div className="p-6">
            <div className="mb-4 p-3 bg-green-100 border border-green-200 rounded-lg">
              <p className="text-xs text-green-800 flex items-center gap-2">
                <SparklesIcon className="h-4 w-4" />
                <span className="font-medium">Modifiez le texte avant de le sauvegarder dans FileMaker</span>
              </p>
            </div>

            <textarea
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              rows={Math.max(14, editedText.split('\n').length + 2)}
              className="w-full px-4 py-3 mb-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm leading-relaxed resize-none"
              style={{ minHeight: '350px' }}
            />

            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending || !editedText.trim()}
                className="flex-1 inline-flex justify-center items-center px-5 py-3 text-sm font-bold rounded-xl text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 shadow-md hover:shadow-lg transition-all"
              >
                {saveMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sauvegarde...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Sauvegarder dans FileMaker
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setGeneratedText(null)
                  setEditedText('')
                  setIsEditingGenerated(false)
                }}
                disabled={saveMutation.isPending}
                className="px-5 py-3 border border-gray-300 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      ) : isCreatingPrompt ? (
        /* FOCUS MODE: Modifying Prompt */
        <div>
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#244886' }}>
                <PencilSquareIcon className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-bold text-gray-900">Modification du prompt</span>
            </div>
            <button
              onClick={() => {
                setIsCreatingPrompt(false)
                setNewPromptText('')
                setNewPromptComment('')
                setIsEditingPrompt(false)
              }}
              className="text-xs hover:underline font-medium"
              style={{ color: '#244886' }}
            >
              Annuler
            </button>
          </div>

          <div className="p-6">
            {activePrompt && (
              <div className="mb-4 p-3 border rounded-lg" style={{ backgroundColor: '#e3eaf3', borderColor: '#244886' }}>
                <p className="text-xs flex items-center gap-2" style={{ color: '#244886' }}>
                  <SparklesIcon className="h-4 w-4" />
                  <span className="font-medium">Le prompt actif a été pré-rempli. Modifiez-le et créez une nouvelle version.</span>
                </p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Texte du prompt *</label>
                <textarea
                  value={newPromptText}
                  onChange={(e) => setNewPromptText(e.target.value)}
                  rows={12}
                  placeholder="Entrez le texte du prompt pour la génération d'appréciation..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent font-mono text-sm"
                  style={{ '--tw-ring-color': '#244886' } as React.CSSProperties}
                />
                <div className="flex justify-between mt-2">
                  <p className="text-xs text-gray-500">Minimum 10 caractères</p>
                  <span className={`text-xs font-semibold ${newPromptText.length < 10 ? 'text-red-600' : 'text-green-600'}`}>
                    {newPromptText.length} caractères
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Commentaire (optionnel)</label>
                <input
                  type="text"
                  value={newPromptComment}
                  onChange={(e) => setNewPromptComment(e.target.value)}
                  placeholder="Décrivez les changements effectués..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': '#244886' } as React.CSSProperties}
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCreatePrompt}
                  disabled={createPrompt.isPending || newPromptText.trim().length < 10}
                  className="flex-1 inline-flex justify-center items-center px-5 py-3 text-sm font-bold rounded-xl text-white disabled:opacity-50 shadow-md hover:shadow-lg"
                  style={{ backgroundColor: '#244886' }}
                >
                  {createPrompt.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Création...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Créer et Activer
                    </>
                  )}
                </button>
              </div>

              {/* Prompt History Section */}
              {promptHistory.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <ClockIcon className="h-4 w-4" />
                      Historique des prompts
                      <span className="text-xs font-normal text-gray-500">
                        ({promptHistory.length} version{promptHistory.length > 1 ? 's' : ''})
                      </span>
                    </h3>
                    <button
                      onClick={() => setShowPromptHistory(!showPromptHistory)}
                      className="text-xs px-3 py-1.5 rounded-lg border font-semibold"
                      style={{ borderColor: '#244886', color: '#244886' }}
                    >
                      {showPromptHistory ? 'Masquer' : 'Afficher'}
                    </button>
                  </div>

                  {showPromptHistory && (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {promptHistory.map((prompt) => {
                        const isExpanded = expandedPromptId === prompt.id
                        return (
                          <div
                            key={prompt.id}
                            className="border border-gray-200 rounded-xl overflow-hidden transition-colors"
                          >
                            <div className="p-3 bg-gradient-to-br from-gray-50 to-white">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  {prompt.comment && (
                                    <div className="mb-1">
                                      <span className="text-xs font-semibold" style={{ color: '#244886' }}>{prompt.comment}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <ClockIcon className="h-3 w-3" />
                                    <span>{formatDate(prompt.createdAt)}</span>
                                    <span>•</span>
                                    <UserIcon className="h-3 w-3" />
                                    <span>{prompt.createdByName}</span>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleActivatePrompt(prompt.id)}
                                    disabled={activatePrompt.isPending}
                                    className="text-xs px-3 py-1.5 text-white font-semibold rounded-lg disabled:opacity-50"
                                    style={{ backgroundColor: '#244886' }}
                                  >
                                    Activer
                                  </button>
                                  <button
                                    onClick={() => setExpandedPromptId(isExpanded ? null : prompt.id)}
                                    className="text-xs px-3 py-1.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300"
                                  >
                                    {isExpanded ? 'Réduire' : 'Voir'}
                                  </button>
                                </div>
                              </div>

                              {isExpanded && (
                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <div className="text-xs text-gray-700 bg-white p-3 rounded-lg border border-gray-200 max-h-48 overflow-y-auto whitespace-pre-wrap">
                                    {prompt.promptText}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* NORMAL MODE: Generation Interface */
        <>
          <div className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <BeakerIcon className="h-5 w-5 text-blue-600" />
                  Génération IA
                </h2>
                <p className="text-xs text-gray-600 mt-1">
                  Générez une appréciation avec GPT-5
                </p>
              </div>
            </div>
          </div>

          <div className="p-5">
            {!affaireId ? (
              <div className="bg-gray-50 rounded-xl p-6 text-center text-gray-600 border border-dashed border-gray-300">
                <BeakerIcon className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                <p className="font-medium">Sélectionnez une affaire pour commencer</p>
              </div>
            ) : (
              <>
                {/* Prompt Section - Collapsible */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      onClick={() => {
                        setShowActivePrompt(!showActivePrompt)
                        if (!showActivePrompt) setShowHistory(false)
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg border font-semibold"
                      style={{ borderColor: '#244886', color: '#244886' }}
                    >
                      {showActivePrompt ? 'Masquer le prompt' : 'Afficher le prompt'}
                    </button>
                    <button
                      onClick={() => {
                        setShowHistory(!showHistory)
                        if (!showHistory) setShowActivePrompt(false)
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg border font-semibold"
                      style={{ borderColor: '#244886', color: '#244886' }}
                    >
                      {showHistory ? 'Masquer l\'historique' : 'Historique'}
                    </button>
                  </div>

                  {showActivePrompt && (
                    <>
                      {activePrompt ? (
                        <div className="bg-gradient-to-br from-blue-50 to-white border rounded-xl p-4" style={{ borderColor: '#244886' }}>
                          {activePrompt.comment && (
                            <div className="mb-2">
                              <span className="text-xs font-semibold" style={{ color: '#244886' }}>{activePrompt.comment}</span>
                            </div>
                          )}
                          <div className="text-xs text-gray-700 bg-white/70 p-3 rounded-lg border border-gray-200 max-h-32 overflow-y-auto">
                            {activePrompt.promptText}
                          </div>
                          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                            <span>{formatDate(activePrompt.createdAt)}</span>
                            <div className="flex items-center gap-2">
                              <span>{activePrompt.createdByName}</span>
                              {!isEditingPrompt && (
                                <button
                                  onClick={() => {
                                    setIsEditingPrompt(true)
                                    setIsCreatingPrompt(true)
                                    setNewPromptText(activePrompt?.promptText || '')
                                    setNewPromptComment('')
                                  }}
                                  className="text-xs px-2 py-1 text-white font-semibold rounded-lg inline-flex items-center gap-1"
                                  style={{ backgroundColor: '#244886' }}
                                >
                                  <PencilSquareIcon className="h-3 w-3" />
                                  Modifier
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                          <p className="text-sm text-yellow-800 font-medium">Aucun prompt actif</p>
                          <button
                            onClick={() => {
                              setIsCreatingPrompt(true)
                              setIsEditingPrompt(true)
                            }}
                            className="mt-2 text-xs text-yellow-700 hover:text-yellow-900 underline"
                          >
                            Créer un prompt
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* History Section */}
                {showHistory && (
                  <div className="mb-4 border border-gray-200 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <ClockIcon className="h-4 w-4" />
                      Historique des générations
                    </h3>
                    {history.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-4">Aucune génération encore</p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {history.map((item, index) => (
                          <div
                            key={item.id}
                            className={`border rounded-lg p-3 cursor-pointer ${
                              index === 0 ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200'
                            }`}
                            onClick={() => setExpandedHistoryId(expandedHistoryId === item.id ? null : item.id)}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600">{formatDate(item.createdAt)}</span>
                              <EyeIcon className="h-4 w-4 text-gray-400" />
                            </div>
                            {expandedHistoryId === item.id ? (
                              <div className="text-xs text-gray-700 mt-2 bg-white rounded p-2 max-h-32 overflow-y-auto">
                                {item.generatedText}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-600 line-clamp-2">{item.generatedText}</div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={!canGenerate || generateMutation.isPending}
                  className="w-full inline-flex justify-center items-center px-5 py-3 text-sm font-bold rounded-xl text-white disabled:opacity-50 disabled:bg-gray-400 shadow-md hover:shadow-lg transition-all"
                  style={{ backgroundColor: canGenerate && !generateMutation.isPending ? '#244886' : '#9CA3AF' }}
                  onMouseEnter={(e) => {
                    if (canGenerate && !generateMutation.isPending) {
                      e.currentTarget.style.backgroundColor = '#1d3a6d'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (canGenerate && !generateMutation.isPending) {
                      e.currentTarget.style.backgroundColor = '#244886'
                    }
                  }}
                >
                  {generateMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="h-5 w-5 mr-2" />
                      Générer l'appréciation
                    </>
                  )}
                </button>

                {generateMutation.isPending && (
                  <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-300 rounded-xl p-4">
                    <p className="text-sm text-blue-900 font-semibold">Traitement en cours par GPT-5...</p>
                    <p className="text-xs text-blue-700 mt-1">Cela peut prendre 30s à 2 minutes. Merci de patienter.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}
